
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Loader2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OtpLogin = ({ mobile, onBack, onSuccess }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [buttonState, setButtonState] = useState('default');
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (newOtp.every(d => d !== '') && index === 5) handleSubmit(newOtp.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    pasted.split('').forEach((digit, idx) => newOtp[idx] = digit);
    setOtp(newOtp);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    if (pasted.length === 6) handleSubmit(pasted);
  };

  const handleSubmit = (otpValue) => {
    setError('');
    if (otpValue.length !== 6) {
      setError('Please enter complete OTP');
      setButtonState('error');
      setTimeout(() => setButtonState('default'), 500);
      return;
    }
    setButtonState('loading');
    setTimeout(() => {
      setButtonState('success');
      setTimeout(onSuccess, 800);
      navigate('/builder');
    }, 1500);
  };

  const handleResend = () => {
    if (!canResend) return;
    setOtp(['', '', '', '', '', '']);
    setResendTimer(30);
    setCanResend(false);
    inputRefs.current[0]?.focus();
    console.log('OTP resent to:', mobile);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 14,
          color: '#D1D5DB',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <ArrowLeft style={{ width: 16, height: 16 }} />
        Change mobile number
      </button>

      {/* Mobile Display */}
      <div
        style={{
          padding: 12,
          background: '#1F2937',
          borderRadius: 16,
          border: '1px solid #4B5563',
        }}
      >
        <p style={{ fontSize: 14, color: '#9CA3AF', margin: 0 }}>OTP sent to</p>
        <p style={{ fontSize: 16, fontWeight: 500, margin: 0, color: '#F3F4F6' }}>{mobile}</p>
      </div>

      {/* OTP Input */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ fontSize: 14, fontWeight: 500, color: '#E5E7EB' }}>Enter 6-digit OTP</label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              style={{
                flex: 1,
                aspectRatio: '1 / 1',
                width: 40, // explicit width
                height: 40, // explicit height
                textAlign: 'center',
                fontSize: 24,
                fontWeight: 'bold',
                color: '#F3F4F6',
                background: '#1F2937',
                border: `2px solid ${error || buttonState === 'error' ? '#EF4444' : '#4B5563'}`,
                borderRadius: 16,
                outline: 'none',
              }}
            />
          ))}
        </div>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ fontSize: 14, color: '#EF4444', margin: 0 }}
          >
            {error}
          </motion.p>
        )}
      </div>

      {/* Resend OTP */}
      <div style={{ textAlign: 'center' }}>
        {canResend ? (
          <button
            onClick={handleResend}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontSize: 14,
              color: '#D1D5DB',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <RefreshCw style={{ width: 16, height: 16 }} />
            Resend OTP
          </button>
        ) : (
          <p style={{ fontSize: 14, color: '#9CA3AF', margin: 0 }}>
            Resend OTP in <span style={{ fontWeight: 500, color: '#E5E7EB' }}>{resendTimer}s</span>
          </p>
        )}
      </div>

      {/* Verify Button */}
      <motion.button
        onClick={() => handleSubmit(otp.join(''))}
        disabled={buttonState === 'loading' || buttonState === 'success'}
        whileHover={buttonState === 'default' ? { scale: 1.02 } : {}}
        whileTap={buttonState === 'default' ? { scale: 0.98 } : {}}
        style={{
          width: '100%',
          padding: '12px 0',
          borderRadius: 16,
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8,
          cursor: buttonState === 'loading' || buttonState === 'success' ? 'not-allowed' : 'pointer',
          background:
            buttonState === 'success'
              ? '#22C55E'
              : buttonState === 'error'
                ? '#EF4444'
                : 'linear-gradient(90deg, #6B7280 0%, #111315 100%)',
          color: '#fff',
        }}
      >
        {buttonState === 'loading' && (
          <>
            <Loader2 style={{ width: 20, height: 20, animation: 'spin 1s linear infinite' }} /> Verifying...
          </>
        )}
        {buttonState === 'success' && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 500 }}>
              <Check style={{ width: 20, height: 20 }} />
            </motion.div>
            Verified!
          </>
        )}
        {(buttonState === 'default' || buttonState === 'error') && 'Verify OTP'}
      </motion.button>

      {/* Help Text */}
      <p style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', margin: 0 }}>
        Didn't receive the code? Check your SMS or try resending.
      </p>
    </div>
  );
};

export default OtpLogin;
 